/**
 * Session types: interaction with an agent to build something.
 * Modeled roughly on the Agent Client Protocol (prompts, responses, tool calls).
 */

import { z } from 'zod';

export const SessionMetaSchema = z.object({
  sessionId: z.string(),
  workspaceId: z.string(),
  title: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type SessionMeta = z.infer<typeof SessionMetaSchema>;

/** ACP-style prompt from user */
export const SessionPromptSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  /** text | resource */
  type: z.enum(['text', 'resource']).default('text'),
  text: z.string().optional(),
  resourceUri: z.string().optional(),
  createdAt: z.string(),
});
export type SessionPrompt = z.infer<typeof SessionPromptSchema>;

/** ACP-style response (message chunk or completion) */
export const SessionResponseSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  promptId: z.string(),
  /** message | completion */
  kind: z.enum(['message', 'completion']),
  content: z.string().optional(),
  /** For completion: stopReason etc. */
  stopReason: z.string().optional(),
  createdAt: z.string(),
});
export type SessionResponse = z.infer<typeof SessionResponseSchema>;

/** Tool / skill call during a turn */
export const SessionToolCallSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  promptId: z.string(),
  name: z.string(),
  params: z.record(z.unknown()).optional(),
  result: z.record(z.unknown()).optional(),
  createdAt: z.string(),
});
export type SessionToolCall = z.infer<typeof SessionToolCallSchema>;

/** Session is associated with one or more threads */
export const SessionThreadRefSchema = z.object({
  sessionId: z.string(),
  threadId: z.string(),
  addedAt: z.string(),
});
export type SessionThreadRef = z.infer<typeof SessionThreadRefSchema>;

/**
 * Canonical `session_turns.error_text` when the user ends a turn without an agent failure
 * (stop, leave queue, cancel before start). Matches CLI `prompt_result` cancel paths.
 */
export const USER_ENDED_SESSION_TURN_ERROR_TEXT = 'Stopped by user' as const;

const LEGACY_USER_ENDED_SESSION_TURN_ERRORS: readonly string[] = ['Removed from queue', 'Cancelled before start'];

/** True when `error_text` indicates an intentional user stop, not a model/infra failure. */
export function isUserEndedSessionTurnErrorText(errorText: string | null | undefined): boolean {
  const raw = (errorText ?? '').trim();
  if (!raw) return false;
  /** CLI may append stderr after the primary message (`Stopped by user\n<stderr>`). */
  const firstLine = raw.split(/\r?\n/)[0]?.trim() ?? '';
  if (firstLine === USER_ENDED_SESSION_TURN_ERROR_TEXT) return true;
  if (LEGACY_USER_ENDED_SESSION_TURN_ERRORS.includes(firstLine)) return true;
  if (raw === USER_ENDED_SESSION_TURN_ERROR_TEXT) return true;
  return LEGACY_USER_ENDED_SESSION_TURN_ERRORS.includes(raw);
}

/** Failed turn that should be treated like “stopped by user” in UI and Slack. */
export function isSessionTurnStoppedByUser(turn: {
  status: string;
  errorText: string | null | undefined;
}): boolean {
  return turn.status === 'failed' && isUserEndedSessionTurnErrorText(turn.errorText);
}

/** Latest turn may use “restart last prompt” (same text, new turn) after any terminal failure, not only user stop. */
export function isSessionTurnEligibleForPromptRestart(turn: { status: string }): boolean {
  return turn.status === 'failed';
}
